import { StringUtils, engineComponent } from "neo-common";
import { BaseElement } from "./BaseElement";
import { StyleManager } from "../../Managers/StyleManager";
import { TimelineStyle } from "../Styles/TimelineStyle";
import { ImageTextCounterElement } from "../Texts/ImageTextCounterElement";
import { ContainerElement } from "./ContainerElement";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { ComponentTypes } from "../../ComponentTypes";
import { SoundManager } from "../../Managers/SoundManager";
import { PlayList } from "../../Sounds/PlayList";
import { SongAttributes } from "../../Sounds/Song";
import { Tweens } from "../../Effects/Tweens";
import { SoundElementAttributes } from "../../../CustomComponents/SoundElement";

/**
 * TimelineElement is element which can create animation flow from object and setting up these elemenets based on frames.
 * TimelineElement need TimelineStyle as definition of animation.
 * Example:
 *   <TimelineElement Class="TimelineElement" Style="TimelineStyle" Loop="true" />
 *
 * @class TimelineElement
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class TimelineElement extends BaseElement {
    static AnimationComplete: string = "animationComplete";

    private _style: TimelineStyle;
    private _elements: ContainerElement;
    private _timer: FrameTimerEventEmitter;
    private _framesArray: Array<number>;
    private _frameCounter: number;
    private _loop: boolean;
    private _winValue: number;
    private _winValueFrom: number;

    private _playDelay: FrameTimerEventEmitter;

    private _outroDelayPlay: FrameTimerEventEmitter;
    private _delayPlayElement: Element;

    private dynamicTimeFrames: number = 0;
    private tempFrames: Object = {};

    _tint: number;

    constructor() {
        super();

        this._frameCounter = 0;
        this._loop = false;

        this._timer = CreateFrameTimer("TimelineElement");
        this._timer.on(FrameTimerEvents.Complete, this.animate.bind(this));

        this._outroDelayPlay = CreateFrameTimer("outroDelayPlay");
        this._outroDelayPlay.on(FrameTimerEvents.Complete, this.outroDelayPlay.bind(this));

        this._playDelay = CreateFrameTimer("playDelay");
        this._playDelay.on(FrameTimerEvents.Complete, this.startAnimate.bind(this));
    }

    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        this._timer.stop();

        if (this._elements.childs != null) {
            for (var key in this._elements.childs) {
                if (this._elements.childs.hasOwnProperty(key)) {
                    if (this._elements.childs[key] instanceof PIXI.DisplayObject) {
                        this._elements.childs[key].destroy(options);
                    }
                    this._elements.childs[key] = null;
                }
            }

            this._elements.childs = null;
        }
    }

    public getElements(): ContainerElement {
        return this._elements;
    }

    set winValue(value: number) {
        this._winValue = value;
    }
    get winValue() {
        return this._winValue;
    }
    set winValueFrom(value: number) {
        this._winValueFrom = value;
    }
    get winValueFrom() {
        return this._winValueFrom;
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public createByNode(node: Element): void {
        super.createByNode(node);

        if (node.hasAttribute(TimelineElementAttributes.LOOP)) {
            this._loop = StringUtils.parseBoolean(node.getAttribute(TimelineElementAttributes.LOOP));
        }

        if (node.hasAttribute(TimelineElementAttributes.STYLE)) {
            this._style = <TimelineStyle>StyleManager.Styles[node.getAttribute(TimelineElementAttributes.STYLE)];
            if (this._style != null) {
                // get all elements and add them to this display list
                if (this._style.container != null) {
                    if (
                        node.hasAttribute(TimelineElementAttributes.CLONE) &&
                        StringUtils.parseBoolean(node.getAttribute(TimelineElementAttributes.CLONE))
                    ) {
                        this._elements = this._style.container.clone();
                    } else {
                        this._elements = this._style.container;
                    }
                    this.addChild(this._elements);

                    if (this._style.frames != null) {
                        this._framesArray = [];

                        for (let key in this._style.frames) {
                            this._framesArray.push(parseInt(key));
                        }
                    } else {
                        if (DEBUG) {
                            this.log("WARNING no timeline");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("WARNING no children");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no style with name: " + node.getAttribute(TimelineElementAttributes.STYLE));
                }
            }
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(TimelineElementAttributes.TINT)) {
            if (this._elements != null && this._elements.children != null && this._elements.children.length > 0) {
                for (let i: number = 0; i < this._elements.children.length; i++) {
                    let child: any = this._elements.children[i];
                    if (child instanceof PIXI.DisplayObject) {
                        if (child.children != null && child.children.length > 0) {
                            for (let j: number = 0; j < child.children.length; j++) {
                                let childOfChild: any = child.children[j];
                                if (
                                    childOfChild instanceof PIXI.Sprite ||
                                    childOfChild instanceof PIXI.extras.AnimatedSprite
                                ) {
                                    if (node.getAttribute(TimelineElementAttributes.TINT) === "0") {
                                        childOfChild.tint = 0xffffff;
                                    } else {
                                        childOfChild.tint = parseInt(
                                            node.getAttribute(TimelineElementAttributes.TINT),
                                            16,
                                        );
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (node.hasAttribute(TimelineElementAttributes.PLAY)) {
            if (!isNaN(parseInt(node.getAttribute(TimelineElementAttributes.PLAY)))) {
                this._playDelay.start(parseInt(node.getAttribute(TimelineElementAttributes.PLAY)));
            } else if (StringUtils.parseBoolean(node.getAttribute(TimelineElementAttributes.PLAY))) {
                this._playDelay.stop();
                if (this._framesArray != null) {
                    this.startAnimate();
                } else {
                    if (DEBUG) {
                        this.log("WARNING can't play because movieClip is null");
                    }
                }
            }
        }

        if (node.hasAttribute("GoToOutro") && StringUtils.parseBoolean(node.getAttribute("GoToOutro"))) {
            this._playDelay.stop();
            this.goToOutro();
        }
    }

    /**
     * Start playing animation.
     * @method play
     * @param winValue number - optional
     * @return void
     */
    play(winValue?: number, winValueFrom?: number, duration?: number): void {
        if (duration !== undefined && duration !== null) {
            for (let key in this._elements.childs) {
                if (this._elements.childs[key] instanceof ImageTextCounterElement) {
                    this._elements.childs[key].duration = duration;
                }
            }
        }
        if (winValue != null) {
            this._winValue = winValue;
        }
        this._winValueFrom = winValueFrom || 0;

        if (this._framesArray != null) {
            this.startAnimate();
        }
    }

    /**
     *
     */
    stop(): void {
        if (this._elements.childs != null) {
            for (let key in this._elements.childs) {
                if (this._elements.childs.hasOwnProperty(key)) {
                    Tweens.removeTweens(this._elements.childs[key], "all");
                    this._elements.childs[key].visible = false;
                    if (this._elements.childs[key] instanceof ImageTextCounterElement) {
                        this._elements.childs[key].stop();
                    }
                }
            }
        }

        this.stopAnimate();
    }

    /**
     * Stop animation and go to last outro state.
     * @method goToOutro
     * @return void
     */
    goToOutro(winValue?: number, outro2: boolean = false): void {
        if (winValue != null) {
            this._winValue = winValue;
        }
        this._timer.stop();

        if (this._style != null && (outro2 ? this._style.outro2 : this._style.outro) != null) {
            let node: Element = outro2 ? this._style.outro2 : this._style.outro;
            if (node != null) {
                if (node.hasAttribute("Emit")) {
                    this.emit(node.getAttribute("Emit"));
                }

                let next: Element = node.firstElementChild;
                if (next != null) {
                    while (next != null) {
                        let element: any = this._elements.childs[next.nodeName];
                        if (element != null) {
                            // send new node to element and set / change new attributes
                            element.updateByNode(next);
                            if (element instanceof ImageTextCounterElement) {
                                if (this._winValue != null) {
                                    element.start(this._winValueFrom || 0, this._winValue, 0);
                                } else {
                                    element.force();
                                }
                            }
                        }
                        next = next.nextElementSibling;
                    }
                }
            }

            if (this._style != null && (outro2 ? this._style.outroSounds2 : this._style.outroSounds) != null) {
                let node2: Element = outro2 ? this._style.outroSounds2 : this._style.outroSounds;
                if (node2 != null) {
                    let next: Element = node2.firstElementChild;
                    if (next != null) {
                        while (next != null) {
                            if (next.hasAttribute(SongAttributes.SONG)) {
                                if (next.hasAttribute(SongAttributes.PLAY)) {
                                    let value: string = next.getAttribute(SongAttributes.PLAY);
                                    let intValue: number = parseInt(value);
                                    if (value === "true") {
                                        if (next.hasAttribute(SongAttributes.FADEIN)) {
                                            PlayList.play(
                                                next.getAttribute(SongAttributes.SONG),
                                                parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                            );
                                        } else {
                                            PlayList.play(next.getAttribute(SongAttributes.SONG));
                                        }
                                    } else if (!isNaN(intValue)) {
                                        this._delayPlayElement = next;
                                        this._outroDelayPlay.stop();
                                        this._outroDelayPlay.start(intValue);
                                    }
                                }
                                if (next.hasAttribute(SongAttributes.LOOP)) {
                                    if (next.hasAttribute(SongAttributes.FADEIN)) {
                                        PlayList.loop(
                                            next.getAttribute(SongAttributes.SONG),
                                            parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                        );
                                    } else {
                                        PlayList.loop(next.getAttribute(SongAttributes.SONG));
                                    }
                                }
                                if (next.hasAttribute(SongAttributes.STOP)) {
                                    if (next.hasAttribute(SongAttributes.FADEOUT)) {
                                        PlayList.stop(
                                            next.getAttribute(SongAttributes.SONG),
                                            parseInt(next.getAttribute(SongAttributes.FADEOUT)),
                                        );
                                    } else {
                                        PlayList.stop(next.getAttribute(SongAttributes.SONG));
                                    }
                                }
                            }
                            next = next.nextElementSibling;
                        }
                    }
                }
            }
        }
    }

    private outroDelayPlay(): void {
        if (this._delayPlayElement != null) {
            if (this._delayPlayElement.hasAttribute(SongAttributes.FADEIN)) {
                PlayList.play(
                    this._delayPlayElement.getAttribute(SongAttributes.SONG),
                    parseInt(this._delayPlayElement.getAttribute(SongAttributes.FADEIN)),
                );
            } else {
                PlayList.play(this._delayPlayElement.getAttribute(SongAttributes.SONG));
            }
        }
    }

    /**
     * Start animate on timeline.
     * @method startAnimate
     * @return void
     */
    private startAnimate(): void {
        this._playDelay.stop();
        this._timer.stop();
        this._frameCounter = 0;

        if (this.children != null) {
            if (this._framesArray[0] === 0) {
                this.animate();
            } else {
                this._timer.start(this._framesArray[0]);
            }
        }
    }

    /**
     * Reset timeline and start animation again.
     * @method reset
     * @return void
     */
    private reset(): void {
        this.startAnimate();
    }

    /**
     * Stop timeline.
     * @method stopAnimate
     * @return void
     */
    private stopAnimate(): void {
        this._timer.stop();
    }

    /**
     * Animate change elements when reach new frame.
     * @method animate
     * @return void
     */
    private animate(): void {
        if (this._framesArray != null) {
            if (this._framesArray[this._frameCounter] != null) {
                this.goToFrame(this._framesArray[this._frameCounter]);
            } else {
                this._timer.stop();
                if (this._loop) {
                    this.reset();
                }
            }
        }
        this._frameCounter++;

        if (this._framesArray[this._frameCounter] != null) {
            this._timer.stop();
            this._timer.start(this._framesArray[this._frameCounter] - this._framesArray[this._frameCounter - 1]);
        } else {
            this.emit(TimelineElement.AnimationComplete);
            this._timer.stop();
            if (this._loop) {
                this.reset();
            }
        }
    }

    /**
     * Go to frame get xml node from frames and set up children to state defined in frames xml node.
     * @method goToFrame
     * @param frame number
     * @return void
     */
    private goToFrame(frame: number) {
        if (this._style != null && this._style.frames != null) {
            let node: Element = this._style.frames[frame.toString()];

            if (node.hasAttribute("Emit")) {
                this.emit(node.getAttribute("Emit"));
            }

            if (node != null && node.childElementCount > 0) {
                let next: Element = node.firstElementChild;
                if (next != null) {
                    while (next != null) {
                        let element: any = this._elements.childs[next.nodeName];
                        if (element != null) {
                            // send new node to element and set / change new attributes
                            element.updateByNode(next);
                            if (element instanceof ImageTextCounterElement && this._winValue != null) {
                                if (element.visible) {
                                    if (this.dynamicTimeFrames > 0) {
                                        element.start(this._winValueFrom || 0, this._winValue, this.dynamicTimeFrames);
                                    } else {
                                        element.start(this._winValueFrom || 0, this._winValue);
                                    }
                                    this._winValue = null;
                                }
                            }
                        }
                        next = next.nextElementSibling;
                    }
                }
            }

            if (this._style != null && this._style.framesSounds != null) {
                let node2: Element = this._style.framesSounds[frame.toString()];
                if (node2 != null) {
                    let next: Element = node2.firstElementChild;
                    if (next != null) {
                        while (next != null) {
                            if (next.hasAttribute(SongAttributes.SONG)) {
                                if (next.hasAttribute(SongAttributes.PLAY)) {
                                    if (next.hasAttribute(SongAttributes.FADEIN)) {
                                        PlayList.play(
                                            next.getAttribute(SongAttributes.SONG),
                                            parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                        );
                                    } else {
                                        PlayList.play(next.getAttribute(SongAttributes.SONG));
                                    }
                                }
                                if (next.hasAttribute(SongAttributes.LOOP)) {
                                    if (next.hasAttribute(SongAttributes.FADEIN)) {
                                        PlayList.loop(
                                            next.getAttribute(SongAttributes.SONG),
                                            parseInt(next.getAttribute(SongAttributes.FADEIN)),
                                        );
                                    } else {
                                        PlayList.loop(next.getAttribute(SongAttributes.SONG));
                                    }
                                }
                                if (next.hasAttribute(SongAttributes.STOP)) {
                                    if (next.hasAttribute(SongAttributes.FADEOUT)) {
                                        PlayList.stop(
                                            next.getAttribute(SongAttributes.SONG),
                                            parseInt(next.getAttribute(SongAttributes.FADEOUT)),
                                        );
                                    } else {
                                        PlayList.stop(next.getAttribute(SongAttributes.SONG));
                                    }
                                }
                            }
                            next = next.nextElementSibling;
                        }
                    }
                }
            }
        }
    }

    /**
     * Set times of frames.
     * @param {number} time
     * @returns {TimelineElement}
     */
    public setDynamicTimeFrames(time: number): TimelineElement {
        this.dynamicTimeFrames = time;

        return this;
    }

    /**
     *
     */
    updateDynamicTimeFrames(): void {
        if (this._style != null) {
            if (this._style.framesOriginal != null) {
                //
                this._framesArray = [];
                this.tempFrames = [];

                for (let key in this._style.framesOriginal) {
                    let values: Array<string> = key.split("+");
                    let ii = key.indexOf("+");

                    let newKey: any = values[0];
                    switch (key[ii]) {
                        case "+":
                            newKey = parseInt(values[0]) + this.dynamicTimeFrames;
                            break;
                    }
                    this.tempFrames[newKey] = this._style.framesOriginal[key];
                }
                this._style.frames = this.tempFrames;
                this.tempFrames = [];
                for (let key in this._style.frames) {
                    this._framesArray.push(parseInt(key));
                }
            } else {
                if (DEBUG) {
                    this.log("WARNING no timeline");
                }
            }

            if (this._style.framesSoundsOriginal != null) {
                this.tempFrames = [];

                for (let key in this._style.framesSoundsOriginal) {
                    let values: Array<string> = key.split("+");
                    let ii = key.indexOf("+");

                    let newKey: any = values[0];
                    switch (key[ii]) {
                        case "+":
                            newKey = parseInt(values[0]) + this.dynamicTimeFrames;
                            break;
                    }
                    this.tempFrames[newKey] = this._style.framesSoundsOriginal[key];
                }
                this._style.framesSounds = this.tempFrames;
                this.tempFrames = [];
            } else {
                if (DEBUG) {
                    this.log("WARNING no timeline");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no style presented ");
            }
        }
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this._timer.stop();
        super.destroy(options);
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return TimelineElement
     */
    clone(): TimelineElement {
        let element: TimelineElement = new TimelineElement();

        element._elements = this._elements.clone();
        element.addChild(element._elements);
        element._style = this._style;
        element._framesArray = this._framesArray;
        element._loop = this._loop;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }

    public setTint(tintColor: number): void {
        if (this._tint === tintColor || isNaN(tintColor)) {
            return;
        }
        this._tint = tintColor;
        for (let child in this._elements.childs) {
            if (typeof this._elements.childs[child].tint === "number") {
                this._elements.childs[child].tint = tintColor;
            }
        }

        // for(let child in this._style.container.childs) {
        //     if(typeof this._style.container.childs[child].tint === "number") {
        //         this._style.container.childs[child].tint = tintColor;
        //     }
        // }
    }

    set tint(tintColor: number) {
        this.setTint(tintColor);
    }

    get tint(): number {
        return this._tint;
    }
}

export const TimelineElementAttributes = {
    CLONE: "Clone",
    DEBUG: "Debug",
    EMIT: "Emit",
    /*
     * Indicates whenever this element should loop when it reaches the end of its timeline.
     * @property Loop
     * @type boolean
     * @default false
     */
    LOOP: "Loop",
    /*
     * Start playing animation.
     * @property Play
     * @type boolean
     */
    PLAY: "Play",
    /*
     * Playlist id for machine.
     * @property Sounds
     * @type string
     * @default null
     */
    SOUNDS: "Sounds",
    /*
     * Style holds elements as ContainerElement and also holds frames as xml nodes.
     * Style for TimelineElement has to be instance of TimelineStyle.
     * @property Style
     * @type string
     * @default null
     */
    STYLE: "Style",
    /*
     * Tint of animation. Hex color which colorize movie clip.
     * @property Tint
     * @type string
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/TimelineElement.ts
 **/